<template>
  <div>
    <div class="row g-3">
      <div class="col-md-12">
        <label class="form-label">
          <FontAwesomeIcon icon="building" class="me-1" />
          {{ $t("production_strategy.recipient_company") }}
        </label>
        <GlobalCompanySearchDropdown
          :modelValue="model.recipientCompanyName"
          :withLabel="false"
          :withDescription="false"
          :invalid="v$.recipientCompanyName.$error"
          @select="handleCompanySelected"
          @update:modelValue="handleCompanyNameChange"
        />
        <div
          v-if="v$.recipientCompanyName.$error"
          class="text-danger small mt-1"
        >
          <span
            v-for="error of v$.recipientCompanyName.$errors"
            :key="error.$uid"
          >
            {{ error.$message }}
          </span>
        </div>
      </div>
      <div class="col-md-6">
        <label class="form-label">
          <FontAwesomeIcon icon="flag" class="me-1" />
          {{ $t("production_strategy.recipient_country") }}
        </label>
        <input
          type="text"
          class="form-control text-uppercase"
          v-model="localRecipientRegistryCountry"
          maxlength="2"
          placeholder="NO, DE, FR..."
          :class="{ 'is-invalid': v$.recipientRegistryCountry.$error }"
        />
        <div class="invalid-feedback" v-if="v$.recipientRegistryCountry.$error">
          <span
            v-for="error of v$.recipientRegistryCountry.$errors"
            :key="error.$uid"
          >
            {{ error.$message }}
          </span>
        </div>
      </div>
      <div class="col-md-6">
        <label class="form-label">
          <FontAwesomeIcon icon="id-card" class="me-1" />
          {{ $t("production_strategy.recipient_eecs_account") }}
        </label>
        <input
          type="text"
          class="form-control font-monospace"
          v-model="localRecipientEecsAccountNumber"
          placeholder="33X1234567"
          :class="{ 'is-invalid': v$.recipientEecsAccountNumber.$error }"
        />
        <div
          class="invalid-feedback"
          v-if="v$.recipientEecsAccountNumber.$error"
        >
          <span
            v-for="error of v$.recipientEecsAccountNumber.$errors"
            :key="error.$uid"
          >
            {{ error.$message }}
          </span>
        </div>
        <EecsNameMismatchWarning
          :companyName="model.recipientCompanyName"
          :accountNumber="model.recipientEecsAccountNumber"
        />
      </div>
      <div class="col-md-6">
        <label class="form-label">
          <FontAwesomeIcon icon="key" class="me-1" />
          {{ $t("production_strategy.recipient_internal_account_number") }}
        </label>
        <input
          type="text"
          class="form-control"
          v-model="localRecipientRegistryInternalAccountNumber"
          :placeholder="
            $t('production_strategy.recipient_internal_account_placeholder')
          "
        />
        <div class="form-text text-muted">
          {{ $t("production_strategy.recipient_internal_account_help") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import EecsNameMismatchWarning from "@/components/common/EecsNameMismatchWarning.vue";
import GlobalCompanySearchDropdown from "@/components/common/GlobalCompanySearchDropdown.vue";
import { useValidators } from "@/composables/useValidators";
import type { PickedCompany } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useVuelidate } from "@vuelidate/core";
import { computed, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";

export interface SendData {
  recipientCompanyName: string;
  recipientRegistryCountry: string;
  recipientEecsAccountNumber: string;
  recipientRegistryInternalAccountNumber?: string;
}

const model = defineModel<SendData>({
  default: () => ({
    recipientCompanyName: "",
    recipientRegistryCountry: "",
    recipientEecsAccountNumber: "",
    recipientRegistryInternalAccountNumber: "",
  }),
});

const emit = defineEmits<{
  companySelected: [company: PickedCompany];
}>();

// Constants
const MAX_COUNTRY_LENGTH = 2;

// Flag to prevent race condition between company selection and manual typing
const isSelectingCompany = ref(false);

// Validation
const { t } = useI18n();
const validators = useValidators(t);

const rules = computed(() => ({
  recipientCompanyName: { required: validators.required },
  recipientRegistryCountry: {
    required: validators.required,
    exactStringLength: validators.exactStringLength(2),
  },
  recipientEecsAccountNumber: { required: validators.required },
}));

const v$ = useVuelidate(rules, model, { $autoDirty: true });

const localRecipientRegistryCountry = computed({
  get: () => model.value.recipientRegistryCountry,
  set: (value: string) => {
    const normalized = value.toUpperCase().slice(0, MAX_COUNTRY_LENGTH);
    model.value = {
      ...model.value,
      recipientRegistryCountry: normalized,
    };
  },
});

const localRecipientEecsAccountNumber = computed({
  get: () => model.value.recipientEecsAccountNumber,
  set: (value: string) => {
    model.value = {
      ...model.value,
      recipientEecsAccountNumber: value,
    };
  },
});

const localRecipientRegistryInternalAccountNumber = computed({
  get: () => model.value.recipientRegistryInternalAccountNumber ?? "",
  set: (value: string) => {
    const trimmed = value.trim();
    model.value = {
      ...model.value,
      recipientRegistryInternalAccountNumber: trimmed,
    };
  },
});

// Handle company selection from dropdown (fills all fields)
const handleCompanySelected = async (company: PickedCompany) => {
  isSelectingCompany.value = true;

  model.value = {
    ...model.value,
    recipientCompanyName: company.name,
    recipientRegistryCountry: company.country,
    recipientEecsAccountNumber: company.account_eecs ?? "",
  };

  emit("companySelected", company);

  // Reset flag after next tick to allow manual typing again
  await nextTick();
  isSelectingCompany.value = false;
};

// Handle manual typing in company name field (only updates company name)
const handleCompanyNameChange = async (companyName: string) => {
  // If company selection is in progress, wait for it to complete
  if (isSelectingCompany.value) {
    await nextTick();
    // If still selecting after next tick, don't update (company selection takes precedence)
    if (isSelectingCompany.value) {
      return;
    }
  }

  // Only update company name, preserve other fields
  model.value = {
    ...model.value,
    recipientCompanyName: companyName,
  };
};

// Expose validation API for parent
defineExpose({
  isValid: () => !v$.value.$invalid,
  touchAndValidate: () => {
    v$.value.$touch();
    return !v$.value.$invalid;
  },
});
</script>

<style scoped lang="scss"></style>
